import { useCallback, useEffect, useRef, useState } from "react"
import { useIntersection } from "@mantine/hooks"

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Count-up figure, HYDRATION-SAFE (act:7079ffd1). Same invariant as
// Reveal.jsx and for the same reason: the funnel landing is server-rendered,
// so the INITIAL render must be identical on server and client — it always
// shows the final value. The old shape initialized to 0 whenever
// IntersectionObserver existed, which made server ("$1,000.00") and first
// client render ("0.00") disagree and failed every hydration (CP1
// automation). The animation now arms only AFTER mount, and only when the
// element is still entirely below the viewport — an invisible reset, followed
// by the same count-up on scroll.
//
// What is and is not mechanically guarded is stated once, in Reveal.jsx
// (act:35de96ff). The short version, because it matters here specifically: the
// THROW class is now gated by a real SSR render in CI, but HYDRATION
// DIVERGENCE — which is this component's actual historical defect — is not.
export default function CountUp({
  to,
  prefix = "",
  suffix = "",
  decimals = 2,
  duration = 1400,
  className = "",
}) {
  const [val, setVal] = useState(to)
  // "Animation consumed" — starts true (displaying the final value needs no
  // animation); the mount effect flips it false only when it arms a run.
  const started = useRef(true)
  const nodeRef = useRef(null)
  const { ref: observe, entry } = useIntersection({ threshold: 0.4 })
  // MEMOIZED, load-bearing — same defect and same reasoning as Reveal.jsx's
  // setRefs (CP3 boundary-man, blocking): an inline combiner re-cycles
  // Mantine's observer every commit into a sustained busy-loop.
  const setRefs = useCallback(
    (node) => {
      nodeRef.current = node
      observe(node)
    },
    [observe],
  )

  // Post-mount arming — client-only and post-hydration by construction, so
  // the reset to 0 is a normal update, never a hydration mismatch. Declared
  // FIRST: the fallback effect below reads started.current on the same mount
  // pass.
  useEffect(() => {
    const canAnimate = typeof IntersectionObserver !== "undefined" && !prefersReducedMotion()
    if (!canAnimate) return
    const rect = nodeRef.current?.getBoundingClientRect()
    if (rect && rect.top > window.innerHeight) {
      started.current = false
      setVal(0)
    }
    // Arm once, on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (started.current || !entry?.isIntersecting) return
    started.current = true
    const start = performance.now()
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1)
      const ease = 1 - (1 - p) ** 3
      setVal(ease * to)
      if (p < 1) requestAnimationFrame(tick)
      else setVal(to)
    }
    requestAnimationFrame(tick)
  }, [entry, to, duration])

  // If the observer never fires after an armed reset, the figure must not
  // stay at 0 forever. Gated on `entry` like Reveal's fallback: a healthy
  // observer delivers an initial entry right after attach, which clears this
  // timer — it fires only when that proof-of-life never arrives. Mount-pass
  // ordering makes the started check correct: the arming effect above is
  // declared first, so started.current is already false here when a reset
  // happened.
  useEffect(() => {
    if (started.current || entry) return
    const fallback = setTimeout(() => {
      if (!started.current) setVal(to)
    }, 1800)
    return () => clearTimeout(fallback)
  }, [to, entry])

  return (
    <span ref={setRefs} className={className}>
      <span className="tnum" aria-hidden="true">
        {prefix}
        {val.toFixed(decimals)}
        {suffix}
      </span>
      <span className="sr-only">
        {prefix}
        {to.toFixed(decimals)}
        {suffix}
      </span>
    </span>
  )
}
